import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { NotesTabNavigationParamList, useGoBack, useNotesTabNavigation } from '../../../../../utils/appNavigationNames';
import useNotes from '../../../../../hooks/api/useNotes';
import NoteDetails from '../../../../shared/Notes/NoteDetails';
import { getIntl } from '../../../../../utils/intl';
import useHasNotePermission from '../../../../../hooks/api/useHasNotePermission';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import SendNoteSideDrawerContent from '../../../../shared/SendNoteSideDrawerContent';
import { useMaybeRenderedSideDrawerToggle } from '../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import { SideDrawerContainer } from '../../../../v2BaseComponents/SideDrawer';

const intl = getIntl('notes-details', {
    transferNote: 'Move Note',
    delete: 'Delete Note',
    shareNote: 'Share Note',
    share: 'Share',
});

export type NoteDetailsScreenOwnProps = {};

export type NoteDetailsScreenProps = NoteDetailsScreenOwnProps & {
    route: RouteProp<NotesTabNavigationParamList, 'details'>;
};

const NoteDetailsScreen: React.FC<NoteDetailsScreenProps> = (props) => {
    const goBack = useGoBack();
    const navigator = useNotesTabNavigation();
    const { isLoading, note, deleteNote, isDeletingNote } = useNotes({
        noteId: props.route.params.noteId,
        onNoteLoadFailure: () => {
            goBack();
        },
    });
    const sendNoteSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'sendUserNote',
        title: intl('shareNote'),
        renderContent: (onClose) => (
            <SendNoteSideDrawerContent noteId={props.route.params.noteId} onClose={onClose} medicalProfileId={null} />
        ),
        mobileOnFocus: () =>
            note
                ? navigator.navigate('send', {
                      noteId: props.route.params.noteId,
                  })
                : undefined,
    });

    const hasNoteEditPermission = useHasNotePermission({
        note: note,
        action: 'edit',
    });

    return (
        <SideDrawerContainer>
            <AnimateLoadedContent isLoading={isDeletingNote || isLoading || !note}>
                {() =>
                    note ? (
                        <NoteDetails
                            note={note}
                            canEdit={!isDeletingNote && !note.isSystemGenerated}
                            onEdit={(defaultTab) => {
                                navigator.navigate('edit', {
                                    noteId: note.noteId,
                                    defaultTab,
                                });
                            }}
                            editActions={[
                                {
                                    icon: 'Send',
                                    title: intl('share'),
                                    onPress: () => {
                                        sendNoteSideDrawer.onOpen();
                                    },
                                    testID: 'send-note-button-action',
                                },
                                {
                                    icon: 'Move',
                                    title: intl('transferNote'),
                                    onPress: () => {
                                        navigator.navigate('transfer', {
                                            noteId: note.noteId,
                                        });
                                    },
                                    isDisabled: isDeletingNote,
                                    show: hasNoteEditPermission,
                                    testID: 'transfer-note-button',
                                    displayInMoreMenu: true,
                                },
                                {
                                    icon: 'Trash2',
                                    title: intl('delete'),
                                    onPress: () => {
                                        deleteNote(props.route.params.noteId);
                                        goBack();
                                    },
                                    isDisabled: isDeletingNote,
                                    show: hasNoteEditPermission,
                                    testID: 'delete-note-button',
                                    displayInMoreMenu: true,
                                },
                            ]}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
            {sendNoteSideDrawer.SideDrawer}
        </SideDrawerContainer>
    );
};

export default React.memo(NoteDetailsScreen);
